import React from 'react'
import { Check, Clock3, Copy, Edit3, ExternalLink, Instagram, RefreshCw, Send, SkipForward } from 'lucide-react'
import SocialVisual from './SocialVisual'

const statusDetails = {
  draft: { label: 'Koncept', className: 'bg-amber-100 text-amber-800' },
  ready: { label: 'Připraveno', className: 'bg-emerald-100 text-emerald-800' },
  posted: { label: 'Publikováno', className: 'bg-blue-100 text-blue-800' },
  skipped: { label: 'Přeskočeno', className: 'bg-slate-200 text-slate-700' },
}

const SocialPostCard = ({ post, busy, onEdit, onCopy, onReplace, onStatus }) => {
  const status = statusDetails[post.status] || statusDetails.draft
  const listing = post.evidence?.listing
  const disabled = Boolean(busy)

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="grid gap-0 xl:grid-cols-[260px_minmax(0,1fr)]">
        <div className="flex justify-center bg-slate-100 p-4 xl:p-5">
          <SocialVisual post={post} compact />
        </div>

        <div className="flex min-w-0 flex-col p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.13em] text-violet-700">
                  <Clock3 className="h-3.5 w-3.5" /> {post.scheduledTime} · {post.slotLabel}
                </span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.className}`}>{status.label}</span>
              </div>
              <h2 className="mt-3 text-lg font-semibold text-slate-950">{post.draft?.headline}</h2>
              <p className="mt-1 text-sm leading-5 text-slate-500">{post.evidence?.selectionReason}</p>
            </div>
            {listing?.listingUrl && (
              <a href={listing.listingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-violet-700 hover:bg-violet-50">
                Zdroj <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-3">
            {(post.draft?.slides || []).slice(1, 4).map((fact, index) => (
              <div key={`${fact}-${index}`} className="rounded-xl bg-slate-50 px-3 py-2.5 text-xs leading-5 text-slate-600">{fact}</div>
            ))}
          </div>

          <div className="mt-auto flex flex-wrap gap-2 pt-5">
            <button type="button" disabled={disabled || post.status === 'posted'} onClick={() => onEdit(post)} className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40">
              <Edit3 className="h-3.5 w-3.5" /> Upravit
            </button>
            <button type="button" disabled={disabled} onClick={() => onCopy(`${post.draft.instagramCaption}\n\n${(post.draft.hashtags || []).join(' ')}`, 'Instagram text zkopírován')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              <Instagram className="h-3.5 w-3.5" /> Kopírovat IG
            </button>
            <button type="button" disabled={disabled} onClick={() => onCopy(post.draft.xText, 'Text pro X zkopírován')} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">
              <Copy className="h-3.5 w-3.5" /> Kopírovat X
            </button>
            {post.status !== 'posted' && post.category !== 'market_pulse' && (
              <button type="button" disabled={disabled} onClick={() => onReplace(post)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                <RefreshCw className={`h-3.5 w-3.5 ${busy === 'replace' ? 'animate-spin' : ''}`} /> Jiný návrh
              </button>
            )}
            {post.status === 'draft' && (
              <button type="button" disabled={disabled} onClick={() => onStatus(post, 'ready')} className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800 disabled:opacity-40">
                <Check className="h-3.5 w-3.5" /> Připraveno
              </button>
            )}
            {post.status === 'ready' && (
              <button type="button" disabled={disabled} onClick={() => onStatus(post, 'posted')} className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-800 disabled:opacity-40">
                <Send className="h-3.5 w-3.5" /> Označit publikované
              </button>
            )}
            {!['posted', 'skipped'].includes(post.status) && (
              <button type="button" disabled={disabled} onClick={() => onStatus(post, 'skipped')} className="ml-auto inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium text-slate-500 hover:bg-slate-100 disabled:opacity-40">
                <SkipForward className="h-3.5 w-3.5" /> Přeskočit
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  )
}

export default SocialPostCard
